/**
 * Account / Billing page — shows current premium status and lets the
 * user (a) link a legacy BCRK-XXXX access code → grandfathered Premium,
 * (b) open the LemonSqueezy Customer Portal to manage their subscription,
 * (c) log out.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, LogOut, Loader2, ShieldCheck, Key, ExternalLink,
  Sparkles, Calendar, Crown, X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import useDailyUsage from "@/hooks/useDailyUsage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  bg: "#050507",
  surface: "#0A0A0D",
  border: "rgba(255,255,255,0.08)",
  text: "#FFFFFF",
  muted: "#71717A",
  accent: "#C8FF00",
  green: "#22FFA3",
  cyan: "#06B6D4",
  red: "#EF4444",
};

const STATUS_LABEL = {
  none: { label: "Free", color: C.muted },
  trialing: { label: "Trial attivo", color: C.cyan },
  active: { label: "Premium attivo", color: C.accent },
  cancelled: { label: "Cancellato", color: "#F6C643" },
  expired: { label: "Scaduto", color: C.red },
  past_due: { label: "Pagamento sospeso", color: C.red },
  lifetime: { label: "Premium a vita", color: "#A78BFA" },
  grandfathered: { label: "Premium a vita (codice legacy)", color: "#22FFA3" },
};

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, getToken, logout,
          refreshMe, claimAccessCode } = useAuth();
  const { usage } = useDailyUsage();

  const [code, setCode] = useState("");
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [claimOk, setClaimOk] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/auth?redirect=/account");
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Re-fetch /me on mount in case the user just completed a checkout
  // (LemonSqueezy webhooks land async — refreshing here forces a re-read).
  useEffect(() => { refreshMe(); }, [refreshMe]);

  if (authLoading || !user) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Loader2 size={28} className="animate-spin" style={{ color: C.accent }} />
      </div>
    );
  }

  const statusInfo = STATUS_LABEL[user.premium_status] || STATUS_LABEL.none;
  const isLifetime = user.is_lifetime || user.premium_status === "grandfathered" || user.premium_status === "lifetime";

  const handleClaim = async (e) => {
    e.preventDefault();
    if (claimBusy) return;
    setClaimBusy(true); setClaimError(null); setClaimOk(null);
    try {
      await claimAccessCode(code.trim().toUpperCase());
      setClaimOk("Codice collegato con successo! Premium a vita sbloccato.");
      setCode("");
    } catch (err) {
      setClaimError(err?.response?.data?.detail || err.message || "Errore");
    } finally {
      setClaimBusy(false);
    }
  };

  const handlePortal = async () => {
    setPortalBusy(true); setPortalError(null);
    try {
      const res = await axios.post(`${API}/billing/portal`,
        { return_url: `${window.location.origin}/account` },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      window.location.href = res.data.url;
    } catch (err) {
      setPortalError(err?.response?.data?.detail || err.message || "Errore");
    } finally {
      setPortalBusy(false);
    }
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text }} className="grid-bg">
      <header style={{
        padding: "1.4rem 1.5rem", borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap",
      }}>
        <button
          onClick={() => navigate("/")}
          data-testid="account-back-home"
          style={{
            background: "transparent", border: `1px solid ${C.border}`,
            color: C.muted, padding: "0.45rem 0.85rem",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
            letterSpacing: "0.16em", textTransform: "uppercase",
            cursor: "pointer", display: "inline-flex", gap: 6, alignItems: "center",
          }}
        >
          <X size={12} /> Torna alla Home
        </button>
        <div style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
          fontSize: "1.05rem", letterSpacing: "0.18em", textTransform: "uppercase",
        }}>
          Account
        </div>
        <button
          onClick={() => { logout(); navigate("/"); }}
          data-testid="account-logout-btn"
          style={{
            background: "transparent", border: `1px solid ${C.red}55`,
            color: C.red, padding: "0.45rem 0.85rem",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
            letterSpacing: "0.16em", textTransform: "uppercase",
            cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          <LogOut size={12} /> Esci
        </button>
      </header>

      <main style={{ maxWidth: 760, margin: "0 auto", padding: "2.5rem 1.5rem 5rem" }}>
        {/* User card */}
        <div data-testid="account-user-card" style={{
          padding: "1.4rem 1.4rem", background: C.surface,
          border: `1px solid ${C.border}`, marginBottom: "1.4rem",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 14, flexWrap: "wrap",
        }}>
          <div>
            <div style={{
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem",
              letterSpacing: "0.18em", color: C.muted, textTransform: "uppercase",
              marginBottom: 6,
            }}>
              Email
            </div>
            <div style={{ fontSize: "1rem", color: C.text, wordBreak: "break-all" }}>
              {user.email}
            </div>
          </div>
          <div style={{
            padding: "0.4rem 0.75rem",
            border: `1px solid ${statusInfo.color}55`,
            background: `${statusInfo.color}10`,
            color: statusInfo.color,
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.78rem", letterSpacing: "0.16em", textTransform: "uppercase",
            display: "inline-flex", alignItems: "center", gap: 6,
          }} data-testid="account-status-badge">
            {isLifetime ? <Crown size={13} /> : user.is_premium ? <ShieldCheck size={13} /> : <Sparkles size={13} />}
            {statusInfo.label}
          </div>
        </div>

        {/* Premium section */}
        <div style={{
          padding: "1.4rem", background: C.surface, border: `1px solid ${C.border}`,
          marginBottom: "1.4rem",
        }}>
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
            letterSpacing: "0.16em", textTransform: "uppercase",
            fontSize: "0.95rem", marginBottom: "1rem", color: C.accent,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <Sparkles size={14} /> Premium
          </div>

          {user.is_premium ? (
            <>
              <div style={{ color: C.muted, fontSize: "0.85rem", lineHeight: 1.6, marginBottom: "1rem" }}>
                {user.premium_status === "lifetime"
                  ? "Hai accesso permanente a tutte le funzioni Premium. Grazie!"
                  : user.premium_status === "grandfathered"
                    ? "Premium a vita sbloccato tramite codice legacy. Tutte le funzioni sono disponibili."
                    : "Il tuo abbonamento è attivo. Gestisci pagamenti, fatture e cancellazione tramite il portale clienti LemonSqueezy."}
              </div>

              {user.stripe_current_period_end && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, marginBottom: "1rem",
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "0.74rem", color: C.muted,
                }}>
                  <Calendar size={12} /> Prossimo rinnovo: {new Date(user.stripe_current_period_end).toLocaleDateString("it-IT")}
                </div>
              )}

              {!isLifetime && (
                <button
                  onClick={handlePortal}
                  disabled={portalBusy}
                  data-testid="account-portal-btn"
                  style={{
                    padding: "0.7rem 1.2rem", background: C.accent, color: "#020202",
                    border: "none", fontFamily: "'Barlow Condensed',sans-serif",
                    fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase",
                    fontSize: "0.85rem", cursor: portalBusy ? "wait" : "pointer",
                    display: "inline-flex", alignItems: "center", gap: 8,
                  }}
                >
                  {portalBusy ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
                  {portalBusy ? "Apertura…" : "Gestisci abbonamento"}
                </button>
              )}
              {portalError && (
                <div style={{
                  marginTop: "0.7rem", padding: "0.5rem 0.7rem",
                  background: "rgba(239,68,68,0.08)", border: `1px solid ${C.red}55`,
                  color: C.red, fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace",
                }}>
                  ⚠ {portalError}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ color: C.muted, fontSize: "0.85rem", lineHeight: 1.6, marginBottom: "1rem" }}>
                Sei sul piano <strong style={{ color: C.text }}>Free</strong>.
                Sblocca BrainLab, Samurai illimitato, Monte Carlo e tutte le altre funzioni avanzate.
              </div>
              <button
                onClick={() => navigate("/pricing")}
                data-testid="account-upgrade-btn"
                style={{
                  padding: "0.75rem 1.3rem", background: C.accent, color: "#020202",
                  border: "none", fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase",
                  fontSize: "0.9rem", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 8,
                  boxShadow: `0 0 22px ${C.accent}44`,
                }}
              >
                <ArrowRight size={14} /> Vedi piani Premium
              </button>
            </>
          )}
        </div>

        {/* Daily usage (only for Free users) */}
        {!user.is_premium && usage && (
          <div data-testid="account-usage-card" style={{
            padding: "1.4rem", background: C.surface, border: `1px solid ${C.border}`,
            marginBottom: "1.4rem",
          }}>
            <div style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
              letterSpacing: "0.16em", textTransform: "uppercase",
              fontSize: "0.95rem", marginBottom: "1rem", color: C.cyan,
            }}>
              Quota giornaliera
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 12 }}>
              {[
                ["Samurai Picker", usage.samurai, "samurai"],
                ["Multipla del Ronin", usage.ronin, "ronin"],
              ].map(([label, info, key]) => (
                <div key={key} style={{
                  padding: "0.8rem 0.9rem", background: "rgba(255,255,255,0.02)",
                  border: `1px solid ${C.border}`,
                }}>
                  <div style={{ fontSize: "0.72rem", color: C.muted, marginBottom: 4 }}>{label}</div>
                  <div style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "1.2rem", color: info.remaining > 0 ? C.accent : C.red,
                  }}>
                    {info.used}/{info.limit}
                  </div>
                  <div style={{ fontSize: "0.62rem", color: C.muted, marginTop: 2 }}>
                    {info.remaining} disponibili oggi
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: "0.66rem", color: C.muted, fontFamily: "'JetBrains Mono',monospace" }}>
              Reset a mezzanotte (Europe/Rome) · {usage.day}
            </div>
          </div>
        )}

        {/* Claim legacy code */}
        {!user.claimed_access_code && user.premium_status !== "grandfathered" && (
          <div style={{
            padding: "1.4rem", background: C.surface, border: `1px solid ${C.border}`,
          }}>
            <div style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
              letterSpacing: "0.16em", textTransform: "uppercase",
              fontSize: "0.95rem", marginBottom: "0.5rem", color: C.green,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <Key size={14} /> Hai un codice BCRK-XXXX?
            </div>
            <div style={{ color: C.muted, fontSize: "0.82rem", lineHeight: 1.5, marginBottom: "1rem" }}>
              Collega il tuo vecchio codice di accesso per ottenere <strong style={{ color: C.green }}>Premium gratis a vita</strong>.
              Ogni codice può essere collegato a un solo account.
            </div>
            <form onSubmit={handleClaim} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="BCRK-XXXX-XXXX"
                data-testid="claim-code-input"
                style={{
                  flex: "1 1 200px", minWidth: 200,
                  padding: "0.65rem 0.85rem",
                  background: "rgba(255,255,255,0.03)",
                  border: `1px solid ${C.border}`,
                  color: C.text,
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.82rem", letterSpacing: "0.06em",
                  outline: "none",
                }}
              />
              <button
                type="submit"
                disabled={claimBusy || !code.trim()}
                data-testid="claim-code-btn"
                style={{
                  padding: "0.65rem 1.1rem", background: C.green, color: "#020202",
                  border: "none", fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase",
                  fontSize: "0.82rem",
                  cursor: claimBusy || !code.trim() ? "not-allowed" : "pointer",
                  opacity: claimBusy || !code.trim() ? 0.6 : 1,
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                {claimBusy ? <Loader2 size={12} className="animate-spin" /> : <Key size={12} />}
                Collega codice
              </button>
            </form>
            {claimError && (
              <div data-testid="claim-error" style={{
                marginTop: "0.7rem", padding: "0.5rem 0.7rem",
                background: "rgba(239,68,68,0.08)", border: `1px solid ${C.red}55`,
                color: C.red, fontSize: "0.74rem", fontFamily: "'JetBrains Mono',monospace",
              }}>
                ⚠ {claimError}
              </div>
            )}
            {claimOk && (
              <div data-testid="claim-success" style={{
                marginTop: "0.7rem", padding: "0.5rem 0.7rem",
                background: "rgba(34,255,163,0.08)", border: `1px solid ${C.green}55`,
                color: C.green, fontSize: "0.74rem", fontFamily: "'JetBrains Mono',monospace",
              }}>
                ✓ {claimOk}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
